import React, { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame, extend } from '@react-three/fiber';
import { OrbitControls, Sphere, shaderMaterial, Sparkles, Grid, Text } from '@react-three/drei';
import { EffectComposer, Bloom, DepthOfField, Vignette } from '@react-three/postprocessing';
import * as THREE from 'three';
import { useTranslation } from 'react-i18next';
import SynapseParticles from './SynapseParticles';
import NeuronHoverCard from './NeuronHoverCard';
import NeuronDetailModal from './NeuronDetailModal';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase-config'; // Corrected import path
import { useAppContext } from '../../AppContext';
import { httpsCallable } from 'firebase/functions';

// --- MOCK USER DATA (Remove this when you have real user data fetching) ---
const mockUsers = {
  'user_1': { displayName: 'DreamerOne', photoURL: 'https://i.pravatar.cc/150?u=user_1', bio: 'Weaving thoughts into the digital tapestry.', badges: [{ id: 'b1', name: 'Founder', icon: '🚀' }] },
  'user_2': { displayName: 'StarSailor', photoURL: 'https://i.pravatar.cc/150?u=user_2', bio: 'Explorer of inner and outer space.', badges: [{ id: 'b2', name: 'Visionary', icon: '💡' }] },
};
// --- END MOCK USER DATA ---

// 1. Define the custom NeuronShaderMaterial
const NeuronShaderMaterial = shaderMaterial(
  {
    color: new THREE.Color(0xffffff),
    time: 0,
    pulseIntensity: 0,
    emissiveIntensity: 1.5,
  },
  // Vertex Shader
  /*glsl*/`
    uniform float time;
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;

    void main() {
      vUv = uv;
      vNormal = normalMatrix * normal;
      vPosition = (modelMatrix * vec4(position, 1.0)).xyz;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // Fragment Shader
  /*glsl*/`
    uniform vec3 color;
    uniform float time;
    uniform float pulseIntensity;
    uniform float emissiveIntensity;
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;

    void main() {
      vec3 lightDirection = normalize(vec3(1.0, 1.0, 1.0));
      float diffuse = max(dot(vNormal, lightDirection), 0.0);
      float grid = smoothstep(0.05, 0.1, sin(vPosition.x * 10.0) * sin(vPosition.y * 10.0) * sin(vPosition.z * 10.0));
      vec3 gridColor = mix(color, vec3(0.1, 0.5, 0.8), grid * 0.5);
      float fresnel = pow(1.0 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
      float glow = sin(time * 5.0 + vPosition.x * 0.5) * 0.5 + 0.5;
      glow *= pulseIntensity * 2.0;
      vec3 finalColor = gridColor * (0.5 + diffuse * 0.5);
      finalColor += color * glow * emissiveIntensity;
      float noise = fract(sin(dot(vPosition.xy, vec2(12.9898, 78.233))) * 43758.5453 * (time * 0.1));
      finalColor += color * noise * 0.1;
      gl_FragColor = vec4(finalColor, 1.0);
    }
  `
);

// 2. Define the custom SynapseShaderMaterial
const SynapseShaderMaterial = shaderMaterial(
  {
    color: new THREE.Color(0xffffff),
    time: 0,
    lineWidth: 1.5,
    isConnectedToSelected: 0,
    flowSpeed: 1.0,
    flowIntensity: 0.0,
  },
  // Vertex Shader
  /*glsl*/`
    uniform float time;
    attribute float lineDistance;
    varying float vLineDistance;
    varying vec3 vPosition;

    void main() {
      vLineDistance = lineDistance;
      vPosition = (modelMatrix * vec4(position, 1.0)).xyz;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // Fragment Shader
  /*glsl*/`
    uniform vec3 color;
    uniform float time;
    uniform float lineWidth;
    uniform float isConnectedToSelected;
    uniform float flowSpeed;
    uniform float flowIntensity;
    varying float vLineDistance;
    varying vec3 vPosition;

    void main() {
      vec3 finalColor = color;
      float glowFactor = 0.0;
      
      // Base pulse for all synapses
      float pulse = sin(vLineDistance * 10.0 - time * 5.0) * 0.5 + 0.5;
      glowFactor += pulse * 0.5;

      // Flow effect based on sharedWordCount (flowIntensity)
      float flow = sin(vLineDistance * 20.0 - time * flowSpeed) * 0.5 + 0.5;
      glowFactor += flow * flowIntensity;

      if (isConnectedToSelected > 0.5) {
        float selectedPulse = sin(vLineDistance * 15.0 - time * 10.0) * 0.5 + 0.5;
        glowFactor += selectedPulse * 1.5;
        finalColor = mix(color, vec3(1.0, 1.0, 0.0), selectedPulse * 0.7);
      }
      finalColor *= (1.0 + glowFactor * 1.5);
      gl_FragColor = vec4(finalColor, 1.0);
    }
  `
);

// 3. Extend the materials
extend({ NeuronShaderMaterial, SynapseShaderMaterial });

// Neuron component
function Neuron({ neuronData, onSelectNode, onHoverNode, onUnhoverNode, isSelected }) {
  const meshRef = useRef();
  const [isHovered, setIsHovered] = useState(false);

  const { position, size, color, sentiment, engagement, infusionLevel } = neuronData;
  
  // Dynamic pulse based on sentiment and infusion level
  const basePulseSpeed = 0.003;
  const basePulseIntensity = 0.15;
  const sentimentFactor = sentiment === 'positive' ? 1.5 : (sentiment === 'negative' ? 0.5 : 1.0);
  const finalPulseSpeed = basePulseSpeed * sentimentFactor;
  const finalPulseIntensity = basePulseIntensity * sentimentFactor + (infusionLevel || 0) * 0.02;

  const finalSize = size + (infusionLevel || 0) * 0.01;

  useFrame(() => {
    const scale = finalSize * (1 + Math.sin(Date.now() * finalPulseSpeed) * finalPulseIntensity);
    meshRef.current.scale.setScalar(scale);
  });

  const handlePointerOver = (e) => {
    e.stopPropagation();
    setIsHovered(true);
    onHoverNode(neuronData, { x: e.clientX, y: e.clientY });
    document.body.style.cursor = 'pointer';
  };

  const handlePointerOut = () => {
    setIsHovered(false);
    onUnhoverNode();
    document.body.style.cursor = 'auto';
  };

  return (
    <group position={position}>
      <Sphere
        ref={meshRef}
        args={[finalSize, 32, 32]}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onClick={() => onSelectNode(neuronData)}
      >
        <meshStandardMaterial 
          color={isSelected ? '#fde047' : (isHovered ? new THREE.Color(color).multiplyScalar(3.5) : color)} 
          emissive={isSelected ? '#fde047' : color}
          emissiveIntensity={isHovered ? 8 : 5 + (infusionLevel || 0) * 0.1}
          toneMapped={false}
        />
      </Sphere>
      {(engagement > 0) && (
        <Sparkles
          count={300 + (infusionLevel || 0) * 10}
          speed={1.2 + engagement * 0.3}
          opacity={1.2 + engagement * 0.15}
          size={finalSize * 6}
          color={color}
          scale={[finalSize * 6, finalSize * 6, finalSize * 6]}
          noise={1.2}
        />
      )}
    </group>
  );
}

// Synapse component
function Synapse({ start, end, sharedWordCount }) {
  const materialRef = useRef();
  const flowSpeed = 0.5 + (sharedWordCount || 0) * 0.1; // Faster flow for more shared words
  const flowIntensity = 0.2 + (sharedWordCount || 0) * 0.05; // More intense glow for more shared words

  useFrame(({ clock }) => {
    if (materialRef.current) {
      materialRef.current.uniforms.time.value = clock.getElapsedTime();
      materialRef.current.uniforms.flowSpeed.value = flowSpeed;
      materialRef.current.uniforms.flowIntensity.value = flowIntensity;
    }
  });

  return <SynapseParticles start={start} end={end} sharedWordCount={sharedWordCount} materialRef={materialRef} />;
}

// Star component for constellations
function Star({ constellationData }) {
    const meshRef = useRef();
    const { position, size, name, themes } = constellationData;
    const baseSize = 1 + Math.log2(size || 1) * 0.5;

    useFrame(() => {
        meshRef.current.rotation.y += 0.001;
        meshRef.current.rotation.x += 0.0005;
    });

    return (
        <group position={[position.x, position.y, position.z]}>
            <Sphere ref={meshRef} args={[baseSize, 32, 32]}>
                <meshStandardMaterial
                    color="#ffd700"
                    emissive="#ffd700"
                    emissiveIntensity={2}
                    toneMapped={false}
                />
            </Sphere>
            <Sparkles
                count={size * 10}
                speed={0.5}
                opacity={1}
                size={baseSize * 1.5}
                color="#fff"
                scale={[baseSize * 4, baseSize * 4, baseSize * 4]}
            />
            <Text
                position={[0, baseSize + 0.5, 0]}
                fontSize={0.5}
                color="white"
                anchorX="center"
                anchorY="middle"
            >
                {name}
            </Text>
        </group>
    );
}

function SoulCore({ souls }) {
    const coreRef = useRef();
    const particlesRef = useRef();

    const coreSize = 0.5 + Math.log10(souls + 1) * 0.5;
    const coreIntensity = 1 + Math.log10(souls + 1) * 2;

    useFrame(({ clock }) => {
        const time = clock.getElapsedTime();
        coreRef.current.scale.setScalar(coreSize * (1 + Math.sin(time * 2) * 0.1));
        if (particlesRef.current) {
            particlesRef.current.rotation.y += 0.001;
        }
    });

    return (
        <group>
            <Sphere ref={coreRef} args={[coreSize, 32, 32]}>
                <meshStandardMaterial
                    color="#ffffff"
                    emissive="#ffffff"
                    emissiveIntensity={coreIntensity}
                    toneMapped={false}
                />
            </Sphere>
            <Sparkles
                ref={particlesRef}
                count={Math.min(500, souls / 10)}
                speed={0.2}
                opacity={0.8}
                size={coreSize * 2}
                color="#ffffff"
                scale={[coreSize * 8, coreSize * 8, coreSize * 8]}
            />
        </group>
    );
}

// Main Neural Network Visualization Component
export default function DreamWeaveNeuralNetwork({ neurons = [], synapses = [], constellations = [], souls = 0 }) {
  const { t } = useTranslation();
  const { appFunctions, currentUserProfile, showToast } = useAppContext();
  const [selectedPair, setSelectedPair] = useState([]);
  const [hoveredNeuron, setHoveredNeuron] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);
  const [authorData, setAuthorData] = useState({});
  const [isForging, setIsForging] = useState(false);
  const [isInfusing, setIsInfusing] = useState(false);

  const starlightEssence = currentUserProfile?.starlightEssence || 0;
  const harmonyScore = currentUserProfile?.harmonyScore || 0;

  const ambientLightRef = useRef();

  useFrame(({ clock }) => {
    // Overall DreamWeave "Breathing" effect
    const time = clock.getElapsedTime();
    const breathIntensity = 0.1 + Math.sin(time * 0.5) * 0.05; // Subtle pulse
    if (ambientLightRef.current) {
      ambientLightRef.current.intensity = 1.5 + breathIntensity * (souls / 100); // Scale with souls
    }
  });

  useEffect(() => {
    const fetchUserData = async (userId) => {
      if (!userId || authorData[userId]) return;
      if (mockUsers[userId]) {
        setAuthorData(prev => ({ ...prev, [userId]: mockUsers[userId] }));
        return;
      }
      try {
        const userDocRef = doc(db, 'artifacts/whispers-of-harmony/public/data/user_profiles', userId);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setAuthorData(prev => ({ ...prev, [userId]: userDoc.data() }));
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    if (hoveredNeuron?.userId) {
      fetchUserData(hoveredNeuron.userId);
    }
  }, [hoveredNeuron, authorData]);

  const handleSelectNode = (neuron) => {
    setSelectedPair(prev => {
        const isAlreadySelected = prev.find(n => n.id === neuron.id);
        if (isAlreadySelected) {
            return prev.filter(n => n.id !== neuron.id);
        }
        if (prev.length < 2) {
            return [...prev, neuron];
        }
        // If 2 are already selected, start a new pair
        return [neuron];
    });
  };

  const handleHoverNode = (neuron, position) => {
    setHoveredNeuron(neuron);
    setHoverPosition(position);
  };

  const handleUnhoverNode = () => {
    setHoveredNeuron(null);
    setHoverPosition(null);
  };

  const handleForgeSynapse = async () => {
    if (selectedPair.length !== 2) return;
    setIsForging(true);
    try {
        const forgeSynapse = httpsCallable(appFunctions, 'forgeSynapse');
        await forgeSynapse({
            dreamWeaveId: 'globalDreamWeave', // This will need to be dynamic later
            neuronA_id: selectedPair[0].id,
            neuronB_id: selectedPair[1].id,
        });
        showToast(t('dreamWeaveNeuralNetwork.forgeSuccess'), "success");
        setSelectedPair([]);
    } catch (error) {
        showToast(t('dreamWeaveNeuralNetwork.forgeError', { message: error.message }), "error");
    } finally {
        setIsForging(false);
    }
  };

  const handleInfuseNeuron = async () => {
    if (selectedPair.length !== 1) return;
    setIsInfusing(true);
    try {
        const infuseNeuron = httpsCallable(appFunctions, 'infuseNeuronWithStarlight');
        await infuseNeuron({
            dreamWeaveId: 'globalDreamWeave', // This will need to be dynamic later
            neuronId: selectedPair[0].id,
            amount: 10, // Fixed amount for now
        });
        showToast(t('dreamWeaveNeuralNetwork.infuseSuccess'), "success");
        setSelectedPair([]);
    } catch (error) {
        showToast(t('dreamWeaveNeuralNetwork.infuseError', { message: error.message }), "error");
    } finally {
        setIsInfusing(false);
    }
  };

  return (
    <div className="w-full h-full relative">
      <div className="absolute top-4 left-4 z-10 bg-gray-900/70 p-4 rounded-lg text-white font-semibold">
        <p>{t('dreamWeaveNeuralNetwork.starlightEssence')}: {starlightEssence}</p>
        <p>{t('dreamWeaveNeuralNetwork.harmonyScore')}: {harmonyScore}</p>
        <p>{t('dreamWeaveNeuralNetwork.souls')}: {souls}</p>
      </div>

      {selectedPair.length === 2 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
            <button 
                onClick={handleForgeSynapse}
                disabled={isForging}
                className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-lg shadow-lg hover:bg-indigo-500 transition-colors disabled:opacity-50"
            >
                {isForging ? t('dreamWeaveNeuralNetwork.forging') : t('dreamWeaveNeuralNetwork.forgeSynapse')}
            </button>
        </div>
      )}

      {selectedPair.length === 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-gray-800/80 p-4 rounded-lg text-white text-center">
            <h3 className="font-bold text-lg mb-2">{t('dreamWeaveNeuralNetwork.infuseTitle')}</h3>
            <p className="text-sm mb-4">{t('dreamWeaveNeuralNetwork.infuseDescription')}</p>
            <button 
                onClick={handleInfuseNeuron}
                disabled={isInfusing}
                className="px-6 py-3 bg-purple-600 text-white font-bold rounded-lg shadow-lg hover:bg-purple-500 transition-colors disabled:opacity-50"
            >
                {isInfusing ? t('dreamWeaveNeuralNetwork.infusing') : t('dreamWeaveNeuralNetwork.infuseButton')}
            </button>
        </div>
      )}

      <Canvas gl={{ powerPreference: 'default', toneMapping: THREE.NoToneMapping }} camera={{ position: [0, 0, 30], fov: 75 }} dispose={null}>
        <color attach="background" args={['#05050a']} />
        <fog attach="fog" args={['#05050a', 35, 50]} />
        <ambientLight ref={ambientLightRef} intensity={1.5} />
        <pointLight position={[10, 10, 10]} intensity={2.5} />
        <directionalLight position={[-5, 5, 5]} intensity={2} />
        <OrbitControls minDistance={2} maxDistance={40} />
        
        <Grid
          position={[0, -10, 0]}
          args={[100, 100]}
          infiniteGrid
          fadeDistance={50}
          fadeStrength={1}
        />

        <EffectComposer>
          <Bloom luminanceThreshold={0.1} luminanceSmoothing={0.9} height={400} intensity={4.0} />
          <Vignette eskil={false} offset={0.1} darkness={1.1} />
        </EffectComposer>

        <SoulCore souls={souls} />

        {neurons.map((neuron) => (
          <Neuron
            key={neuron.id}
            neuronData={neuron}
            onSelectNode={handleSelectNode}
            onHoverNode={handleHoverNode}
            onUnhoverNode={handleUnhoverNode}
            isSelected={selectedPair.some(n => n.id === neuron.id)}
          />
        ))}

        {constellations.map((constellation) => (
            <Star key={constellation.id} constellationData={constellation} />
        ))}

        {synapses.map((synapse) => (
          <Synapse
            key={synapse.id}
            start={synapse.start}
            end={synapse.end}
            sharedWordCount={synapse.sharedWordCount}
          />
        ))}
      </Canvas>

      <NeuronHoverCard 
        neuron={hoveredNeuron} 
        user={authorData[hoveredNeuron?.userId]}
        position={hoverPosition} 
      />
    </div>
  );
}
